import type { APIError } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { ClockRewindIcon, PlusIcon, SortAlphabeticalIcon } from '@vizit/icons';
import { BenchmarkCardGrid, BenchmarkDeleteDialog, BenchmarkEditDialog, ClearableSelect } from '@vizit/organisms';
import type { ClearableSelectItem } from '@vizit/organisms';
import { ProductCategoryComboBox, SearchTextField } from '@vizit/organisms';
import { colors, cx } from '@vizit/theme';
import type * as React from 'react';
import { useBenchmarkActions } from '../../hooks/use-benchmark-actions';
import { useProductCategoryList } from '../collection-grid-filters/collection-grid-filters.hooks';
import type { BenchmarkGridFilterOptions, BenchmarkGridFilters } from './benchmark-grid.hooks';
import { BenchmarkSortOption, useBenchmarkGrid } from './benchmark-grid.hooks';

const SORT_ICON_COLOR = colors['gray-utility']['400'];

const BENCHMARK_SORT_ITEMS: ClearableSelectItem[] = [
  {
    id: BenchmarkSortOption.Newest,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkSortOption.Oldest,
    label: 'Oldest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkSortOption.Alphabetical,
    label: 'Alphabetical (A-Z)',
    icon: <SortAlphabeticalIcon size={14} className="text-gray-utility-400" aria-hidden />,
  },
];

interface BenchmarkGridProps {
  /**
   * Workspace whose benchmarks are listed
   */
  workspaceId?: string;
  /**
   * Organization used to scope the category filter
   */
  organizationId?: string;
  /**
   * Current search, category and sort filters, kept in the URL by the route
   */
  filters?: BenchmarkGridFilters;
  /**
   * Called to change one filter; `undefined` clears it
   */
  onFilterChange?: (filter: BenchmarkGridFilterOptions, value?: string) => void;
  /**
   * Shown first in the toolbar, e.g. the Collections / Benchmarks switch
   */
  leadingFilterContent?: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   */
  className?: string;
  /**
   * The page URL of a benchmark; makes each card a link to it and backs "Open in new tab"
   */
  getBenchmarkHref?: (id: string) => string;
  /**
   * Called on a plain click of a card, to navigate without a page load
   */
  onNavigateToBenchmark?: (id: string) => void;
  /**
   * Called when "Create benchmark" is pressed
   */
  onCreateBenchmark?: () => void;
  /**
   * Called when the category list reports an authentication error
   */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * The Library's Benchmarks view: a toolbar to search by name, filter by
 * category and sort, then the matching benchmarks as cards.
 */
function BenchmarkGrid(props: BenchmarkGridProps) {
  const {
    workspaceId,
    organizationId,
    filters,
    onFilterChange,
    leadingFilterContent,
    className,
    getBenchmarkHref,
    onNavigateToBenchmark,
    onCreateBenchmark,
    onAuthenticationError,
  } = props;

  const {
    benchmarks,
    isLoading,
    isError,
    onRetry,
    searchTerm,
    productCategoryId,
    productCategoryName,
    sortOption,
    onSearchTermChange,
    onFilterProductCategory,
    onSortOptionChange,
  } = useBenchmarkGrid({ workspaceId, filters, onFilterChange, onAuthenticationError });

  const actions = useBenchmarkActions({ workspaceId, benchmarks, getBenchmarkHref });

  const createBenchmarkButton = (
    <Button
      level={ButtonLevel.Primary}
      size={ButtonSize.Lg}
      iconStart={<PlusIcon size={20} aria-hidden />}
      onPress={onCreateBenchmark}
    >
      Create benchmark
    </Button>
  );

  const categoryList = useProductCategoryList({
    organizationId,
    workspaceId,
    filterText: productCategoryName,
    onAuthenticationError,
    onFilterProductCategory,
  });

  return (
    <div className={cx('flex min-h-0 w-full flex-1 flex-col', className)}>
      <div className="flex flex-row items-center justify-between gap-6 pb-8">
        <div className="flex flex-row flex-wrap items-center gap-6">
          {leadingFilterContent}
          <SearchTextField
            className="flex-1"
            data-testid="library-benchmarks-search"
            onSearch={onSearchTermChange}
            defaultValue={searchTerm}
            variant="filled"
            placeholder="Search benchmarks by name"
            inputProps={{ className: 'pr-10' }}
          />
          <ProductCategoryComboBox
            // The combobox keeps its text in local state and only resets it when the category is
            // cleared. Remounting per category keeps it in step when the URL changes it (Back/Forward),
            // and the URL's name is the starting text so it never shows the previous category.
            key={productCategoryId ?? 'all-categories'}
            data-testid="library-benchmarks-category-filter"
            filterText={productCategoryName || categoryList.filterText}
            setFilterText={categoryList.setFilterText}
            items={categoryList.items}
            selectedKey={productCategoryId}
            onSelectionChange={categoryList.onSelectionChange}
            isLoading={categoryList.isLoading}
            loadMore={categoryList.loadMore}
          />
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Primary}>
            Sort by:
          </Text>
          <ClearableSelect
            ariaLabel="Benchmark sort"
            placeholder="Newest"
            items={BENCHMARK_SORT_ITEMS}
            selectedKey={sortOption}
            onSelectionChange={(key) => key && onSortOptionChange(key as BenchmarkSortOption)}
            includeAllOption={false}
            isClearable={false}
            className="min-w-[233px]"
            data-testid="library-benchmarks-sort-filter"
          />
        </div>
      </div>

      <div className="min-h-0 w-full flex-1 overflow-y-auto pb-32">
        <BenchmarkCardGrid
          benchmarks={benchmarks}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          getBenchmarkHref={getBenchmarkHref}
          onBenchmarkNavigate={onNavigateToBenchmark}
          onBenchmarkAction={actions.onBenchmarkAction}
          disabledBenchmarkActions={actions.disabledActions}
          headerAction={createBenchmarkButton}
          emptyStateContent={createBenchmarkButton}
        />
      </div>

      <BenchmarkEditDialog
        isOpen={!!actions.editing}
        onOpenChange={actions.onEditOpenChange}
        initialValues={{ name: actions.editing?.name ?? '', description: actions.editing?.description ?? null }}
        errorMessage={actions.editErrorMessage}
        onSubmit={actions.onSubmitEdit}
      />
      <BenchmarkDeleteDialog
        isOpen={!!actions.deleting}
        onOpenChange={actions.onDeleteOpenChange}
        benchmarkName={actions.deleting?.name ?? ''}
        affectedCollections={actions.affectedCollections}
        affectedCollectionCount={actions.affectedCollectionCount}
        errorMessage={actions.deleteErrorMessage}
        onDelete={actions.onConfirmDelete}
      />
    </div>
  );
}

export { BenchmarkGrid };
export type { BenchmarkGridProps };
